import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { GigWorkerClient } from '../../../infrastructure/GigWorkerClient';
import type { GigOrderReference, GigWorkerAdminStats, GigWorkerDetail } from '@/models/GigWorker';
import GigOrderDetailModal from './GigOrderDetailModal';

const formatKrw = (value: number): string => `₩${value.toLocaleString()}`;

export default function GigWorkersPage() {
  const client = useMemo(() => new GigWorkerClient(getAdminApiUrl(AdminApiDomain.GIGWORK), getConfig().apiKey), []);
  const detailRequestId = useRef(0);
  const [workers, setWorkers] = useState<GigWorkerAdminStats[]>([]);
  const [settlementMonths, setSettlementMonths] = useState({ previousMonth: '', currentMonth: '' });
  const [detail, setDetail] = useState<GigWorkerDetail | null>(null);
  const [orderHistoryFilter, setOrderHistoryFilter] = useState('');
  const [selectedGigOrder, setSelectedGigOrder] = useState<GigOrderReference | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [payoutWorker, setPayoutWorker] = useState<GigWorkerAdminStats | null>(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMemo, setPayoutMemo] = useState('');
  const [payoutRequestId, setPayoutRequestId] = useState('');
  const [isPayingOut, setIsPayingOut] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [approvingWorkerId, setApprovingWorkerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editWorker, setEditWorker] = useState<GigWorkerAdminStats | null>(null);
  const [editKakaoName, setEditKakaoName] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const pendingWorkers = useMemo(() => workers.filter((worker) => worker.status === 'PENDING'), [workers]);
  const activeWorkers = useMemo(() => workers.filter((worker) => worker.status !== 'PENDING'), [workers]);
  const totals = useMemo(
    () =>
      workers.reduce(
        (acc, worker) => ({
          completed: acc.completed + worker.completedOrders,
          claimed: acc.claimed + worker.claimedOrders,
          skipped: acc.skipped + worker.skipCount,
        }),
        { completed: 0, claimed: 0, skipped: 0 },
      ),
    [workers],
  );
  const filteredOrders = useMemo(() => {
    const query = orderHistoryFilter.trim().toLowerCase();
    if (!detail || query === '') {
      return detail?.orders ?? [];
    }
    return detail.orders.filter(
      (order) =>
        order.orderId.toLowerCase().includes(query) ||
        order.omsOrderRefs.some((omsOrderRef) => omsOrderRef.toLowerCase().includes(query)),
    );
  }, [detail, orderHistoryFilter]);
  const payoutAmountKrw = Number(payoutAmount);
  const isPayoutAmountValid =
    payoutWorker !== null &&
    payoutRequestId !== '' &&
    Number.isSafeInteger(payoutAmountKrw) &&
    payoutAmountKrw > 0 &&
    payoutAmountKrw <= payoutWorker.outstandingKrw;

  const loadWorkers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await client.listWorkers();
      setWorkers(result.workers);
      setSettlementMonths({ previousMonth: result.previousMonth, currentMonth: result.currentMonth });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load gig workers');
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void loadWorkers();
  }, [loadWorkers]);

  const showDetail = async (workerId: string) => {
    const requestId = ++detailRequestId.current;
    setIsDetailLoading(true);
    setError(null);
    try {
      const result = await client.getWorkerDetail(workerId);
      if (requestId === detailRequestId.current) {
        setDetail(result);
        setOrderHistoryFilter('');
      }
    } catch (detailError) {
      if (requestId === detailRequestId.current) {
        setError(detailError instanceof Error ? detailError.message : 'Failed to load worker detail');
      }
    } finally {
      if (requestId === detailRequestId.current) {
        setIsDetailLoading(false);
      }
    }
  };

  const openPayout = (worker: GigWorkerAdminStats) => {
    setPayoutWorker(worker);
    setPayoutAmount(worker.payableThroughPreviousMonthKrw > 0 ? String(worker.payableThroughPreviousMonthKrw) : '');
    setPayoutMemo('');
    setPayoutRequestId(window.crypto.randomUUID());
    setPayoutError(null);
  };

  const payout = async () => {
    if (!payoutWorker || !isPayoutAmountValid) {
      return;
    }
    setIsPayingOut(true);
    setError(null);
    setPayoutError(null);
    setSuccess(null);
    try {
      const result = await client.payoutWorker(payoutWorker.workerId, {
        requestId: payoutRequestId,
        amountKrw: payoutAmountKrw,
        ...(payoutMemo.trim() ? { memo: payoutMemo.trim() } : {}),
      });
      setPayoutWorker(null);
      setSuccess(`Payout recorded: ${formatKrw(result.paidKrw)} · Outstanding ${formatKrw(result.outstandingKrw)}`);
      await loadWorkers();
    } catch (caughtError) {
      setPayoutError(caughtError instanceof Error ? caughtError.message : 'Failed to record payout');
    } finally {
      setIsPayingOut(false);
    }
  };

  const openEdit = (worker: GigWorkerAdminStats) => {
    setEditWorker(worker);
    setEditKakaoName(worker.kakaoName ?? '');
    setEditMemo(worker.adminMemo ?? '');
  };

  const saveEdit = async () => {
    if (!editWorker) {
      return;
    }
    setIsSavingEdit(true);
    setError(null);
    setSuccess(null);
    try {
      // Always send both fields (empty -> null clears) so a save can't accidentally wipe the other.
      await client.updateWorker(editWorker.workerId, {
        kakaoName: editKakaoName.trim() || null,
        adminMemo: editMemo.trim() || null,
      });
      setEditWorker(null);
      setSuccess('Worker updated');
      await loadWorkers();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update worker');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const approve = async (worker: GigWorkerAdminStats) => {
    // Approval is irreversible — confirm before proceeding.
    if (!window.confirm('Approve this worker and issue a token?')) {
      return;
    }
    setApprovingWorkerId(worker.workerId);
    setError(null);
    setSuccess(null);
    try {
      await client.approveWorker(worker.workerId);
      await loadWorkers();
    } catch (approveError) {
      setError(approveError instanceof Error ? approveError.message : 'Failed to approve worker');
    } finally {
      setApprovingWorkerId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" aria-label="Loading gig workers" />
      </div>
    );
  }

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h2 className="mb-1">Gig Workers</h2>
        <Button
          variant="outline-secondary"
          onClick={() => void loadWorkers()}
          disabled={isPayingOut || approvingWorkerId !== null}
        >
          Reload
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="row g-3 mb-3" role="group" aria-label="Status overview">
        {[
          { label: 'Completed', value: totals.completed.toLocaleString() },
          { label: 'Claimed', value: totals.claimed.toLocaleString() },
          { label: 'Skipped', value: totals.skipped.toLocaleString() },
        ].map((stat) => (
          <div key={stat.label} className="col-12 col-sm-4">
            <Card className="h-100 text-center">
              <Card.Body className="py-3 px-2">
                <div className="text-muted small text-uppercase">{stat.label}</div>
                <div className="fs-4 fw-semibold">{stat.value}</div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Pending approval ({pendingWorkers.length})</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered hover size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {pendingWorkers.map((worker) => (
                <tr key={worker.workerId}>
                  <td>{worker.displayName}</td>
                  <td>
                    <div className="d-flex justify-content-end">
                      <Button
                        size="sm"
                        variant="success"
                        disabled={approvingWorkerId !== null}
                        onClick={() => void approve(worker)}
                      >
                        {approvingWorkerId === worker.workerId ? 'Approving…' : 'Approve'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {pendingWorkers.length === 0 && (
                <tr>
                  <td colSpan={2} className="text-center text-muted py-4">
                    No pending registrations.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="fw-semibold">Workers ({activeWorkers.length})</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered hover size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>KakaoTalk</th>
                <th>Status</th>
                <th className="text-end">Completed</th>
                <th className="text-end">Claimed</th>
                <th className="text-end">Skipped</th>
                <th className="text-end">
                  Last month
                  <div className="fw-normal small text-muted">{settlementMonths.previousMonth}</div>
                </th>
                <th className="text-end">
                  This month
                  <div className="fw-normal small text-muted">{settlementMonths.currentMonth}</div>
                </th>
                <th className="text-end">Credited</th>
                <th className="text-end">Paid out</th>
                <th className="text-end">Outstanding</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {activeWorkers.map((worker) => (
                <tr key={worker.workerId}>
                  <td>{worker.displayName}</td>
                  <td>
                    {worker.kakaoName ?? <span className="text-muted">—</span>}
                    {worker.adminMemo && (
                      <div
                        className="text-muted small text-truncate"
                        style={{ maxWidth: '16rem' }}
                        title={worker.adminMemo}
                      >
                        📝 {worker.adminMemo}
                      </div>
                    )}
                  </td>
                  <td>{worker.status}</td>
                  <td className="text-end">{worker.completedOrders.toLocaleString()}</td>
                  <td className="text-end">{worker.claimedOrders.toLocaleString()}</td>
                  <td className="text-end">{worker.skipCount.toLocaleString()}</td>
                  <td className="text-end">{formatKrw(worker.previousMonthCreditedKrw)}</td>
                  <td className="text-end">{formatKrw(worker.currentMonthCreditedKrw)}</td>
                  <td className="text-end">{formatKrw(worker.creditedKrw)}</td>
                  <td className="text-end">{formatKrw(worker.paidOutKrw)}</td>
                  <td className="text-end">{formatKrw(worker.outstandingKrw)}</td>
                  <td>
                    <div className="d-flex gap-2 justify-content-end">
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(worker)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => void showDetail(worker.workerId)}>
                        Detail
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={worker.outstandingKrw <= 0 || isPayingOut}
                        onClick={() => openPayout(worker)}
                      >
                        Record payout
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {activeWorkers.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center text-muted py-4">
                    No gig workers.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {isDetailLoading && (
        <div className="d-flex justify-content-center py-3">
          <Spinner animation="border" size="sm" role="status" aria-label="Loading worker detail" />
        </div>
      )}

      {detail && !isDetailLoading && (
        <Card className="mt-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold">{detail.displayName} — orders &amp; ledger</span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                detailRequestId.current += 1;
                setDetail(null);
                setIsDetailLoading(false);
              }}
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-2">
              <h6 className="mb-0">
                Order history ({filteredOrders.length}/{detail.orders.length})
              </h6>
              <Form.Group controlId="gig-worker-order-history-filter" style={{ minWidth: 280 }}>
                <Form.Label className="visually-hidden">Filter worker order history</Form.Label>
                <Form.Control
                  type="search"
                  size="sm"
                  aria-label="Filter worker order history"
                  placeholder="Filter by GIG order or OMS ref"
                  value={orderHistoryFilter}
                  onChange={(event) => setOrderHistoryFilter(event.target.value)}
                />
              </Form.Group>
            </div>
            <Table responsive bordered size="sm" className="mb-4 align-middle">
              <thead className="table-light">
                <tr>
                  <th>GIG Order ID</th>
                  <th>Source OMS orders</th>
                  <th className="text-end">Units</th>
                  <th>Status</th>
                  <th className="text-end">Reward</th>
                  <th>Tracking</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.orderId}>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-primary text-decoration-none font-monospace"
                        title="View order handling detail"
                        onClick={() => setSelectedGigOrder({ orderId: order.orderId, omsOrderRef: order.omsOrderRef })}
                      >
                        {order.orderId}
                      </Button>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        {order.omsOrderRefs.map((omsOrderRef) => (
                          <Link
                            key={omsOrderRef}
                            to={`/oms/orders/${omsOrderRef}`}
                            className="text-primary text-decoration-none"
                          >
                            {omsOrderRef}
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="text-end">{order.unitCount}</td>
                    <td>{order.status}</td>
                    <td className="text-end">{formatKrw(order.rewardKrw)}</td>
                    <td>{order.trackingNumber ?? '—'}</td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      {detail.orders.length === 0 ? 'No orders.' : 'No matching orders.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <h6>Ledger ({detail.ledger.length})</h6>
            <Table responsive bordered size="sm" className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Type</th>
                  <th className="text-end">Amount</th>
                  <th>Order</th>
                  <th>Memo / reason</th>
                  <th>Issued by</th>
                  <th>Issued at</th>
                </tr>
              </thead>
              <tbody>
                {detail.ledger.map((line) => (
                  <tr key={line.entryId}>
                    <td>{line.entryType}</td>
                    <td className="text-end">{formatKrw(line.amountKrw)}</td>
                    <td>{line.orderId ?? '—'}</td>
                    <td>{line.reason ?? '—'}</td>
                    <td>
                      {line.adminName ?? line.adminEmail ?? '—'}
                      {line.adminName && line.adminEmail && <div className="small text-muted">{line.adminEmail}</div>}
                    </td>
                    <td>{line.createdAt ? new Date(line.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {detail.ledger.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      No ledger entries.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <GigOrderDetailModal client={client} reference={selectedGigOrder} onHide={() => setSelectedGigOrder(null)} />

      <Modal
        show={payoutWorker !== null}
        onHide={() => {
          if (!isPayingOut) {
            setPayoutWorker(null);
          }
        }}
        aria-labelledby="gig-worker-payout-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="gig-worker-payout-title" className="fs-6">
            Record payout — {payoutWorker?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small">
            This records a payout made manually outside the system. It does not send money.
          </Alert>
          {payoutError && (
            <Alert variant="danger" className="small" role="alert">
              {payoutError}
            </Alert>
          )}
          {payoutWorker && (
            <>
              <dl className="row small mb-3">
                <dt className="col-6 text-muted">Credited</dt>
                <dd className="col-6 text-end">{formatKrw(payoutWorker.creditedKrw)}</dd>
                <dt className="col-6 text-muted">Paid out</dt>
                <dd className="col-6 text-end">{formatKrw(payoutWorker.paidOutKrw)}</dd>
                <dt className="col-6 text-muted">Outstanding</dt>
                <dd className="col-6 text-end fw-semibold">{formatKrw(payoutWorker.outstandingKrw)}</dd>
              </dl>
              <div className="d-flex gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setPayoutAmount(String(payoutWorker.payableThroughPreviousMonthKrw));
                    setPayoutRequestId(window.crypto.randomUUID());
                  }}
                >
                  Through last month ({formatKrw(payoutWorker.payableThroughPreviousMonthKrw)})
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setPayoutAmount(String(payoutWorker.outstandingKrw));
                    setPayoutRequestId(window.crypto.randomUUID());
                  }}
                >
                  Full balance
                </Button>
              </div>
              <Form.Group className="mb-3" controlId="gig-worker-payout-amount">
                <Form.Label>Amount (KRW)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={payoutWorker.outstandingKrw}
                  step={1}
                  value={payoutAmount}
                  isInvalid={payoutAmount !== '' && !isPayoutAmountValid}
                  onChange={(event) => {
                    setPayoutAmount(event.target.value);
                    setPayoutRequestId(window.crypto.randomUUID());
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Enter a whole amount between ₩1 and {formatKrw(payoutWorker.outstandingKrw)}.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="gig-worker-payout-memo">
                <Form.Label>Memo (optional)</Form.Label>
                <Form.Control
                  value={payoutMemo}
                  maxLength={200}
                  onChange={(event) => {
                    setPayoutMemo(event.target.value);
                    setPayoutRequestId(window.crypto.randomUUID());
                  }}
                />
              </Form.Group>
              <div className="d-flex justify-content-between border-top pt-3">
                <span className="text-muted">Outstanding after payout</span>
                <strong>{isPayoutAmountValid ? formatKrw(payoutWorker.outstandingKrw - payoutAmountKrw) : '—'}</strong>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPayoutWorker(null)} disabled={isPayingOut}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void payout()} disabled={!isPayoutAmountValid || isPayingOut}>
            {isPayingOut ? 'Recording…' : 'Record payout'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={editWorker !== null}
        onHide={() => {
          // Ignore X / backdrop dismissal mid-save so an in-flight close can't clobber a re-opened worker.
          if (!isSavingEdit) {
            setEditWorker(null);
          }
        }}
        aria-labelledby="gig-worker-edit-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="gig-worker-edit-title" className="fs-6">
            Edit {editWorker?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>KakaoTalk name</Form.Label>
            <Form.Control
              value={editKakaoName}
              maxLength={100}
              onChange={(event) => setEditKakaoName(event.target.value)}
              placeholder="—"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Memo</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={editMemo}
              maxLength={2000}
              onChange={(event) => setEditMemo(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditWorker(null)} disabled={isSavingEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void saveEdit()} disabled={isSavingEdit}>
            {isSavingEdit ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
